import { useT } from '@dia/i18n';

/**
 * The island as a static SVG sketch (Phase 1). It is drawn from the brand
 * tokens, with flat shapes and no gradients.
 *
 * What is true: the sun rises in the east and sets in the west, and the
 * property has a hacienda, a bungalow, a solar pavilion, a pool and hot tub,
 * docks and a mangrove edge. What is a guess: where each one sits. The caption
 * under the map says so, and the guess is on the placeholder list until Tim
 * replaces the shapes with the real survey.
 */
export function IslandMap() {
  const { t } = useT();

  const label = 'fill-ink text-[11px]';
  const soft = 'fill-inkFaint text-[10px]';

  return (
    <svg
      role="img"
      aria-label={t('guest.island.map_label')}
      viewBox="0 0 400 360"
      className="w-full h-auto rounded border border-line"
    >
      <rect width="400" height="360" className="fill-surfaceDeep" />

      {/* open water */}
      <text x="200" y="26" textAnchor="middle" className={soft}>
        {t('guest.island.label.water')}
      </text>

      {/* east and west, with the sun's two sides */}
      <circle cx="372" cy="60" r="7" className="fill-mark" />
      <text x="372" y="84" textAnchor="middle" className={label}>
        {t('guest.island.label.sunrise')}
      </text>
      <text x="372" y="98" textAnchor="middle" className={soft}>
        {t('guest.island.label.east')}
      </text>
      <circle cx="28" cy="60" r="7" className="fill-mark" opacity="0.55" />
      <text x="28" y="84" textAnchor="middle" className={label}>
        {t('guest.island.label.sunset')}
      </text>
      <text x="28" y="98" textAnchor="middle" className={soft}>
        {t('guest.island.label.west')}
      </text>

      {/* the island */}
      <path
        d="M96 190 C 88 140 128 104 176 100 C 214 78 268 92 292 128 C 322 146 330 196 304 232 C 284 268 232 286 190 280 C 138 286 102 246 96 190 Z"
        className="fill-ground stroke-line"
        strokeWidth="1.5"
      />

      {/* the mangrove edge, a dashed margin along the southern shore */}
      <path
        d="M118 244 C 148 270 192 278 230 272 C 262 266 284 250 296 234"
        fill="none"
        className="stroke-accent"
        strokeWidth="2"
        strokeDasharray="2 5"
        strokeLinecap="round"
      />
      <text x="206" y="304" textAnchor="middle" className={label}>
        {t('guest.island.label.mangrove')}
      </text>

      {/* the houses */}
      <rect x="148" y="150" width="44" height="26" rx="3" className="fill-surface stroke-accent" strokeWidth="1.5" />
      <text x="170" y="196" textAnchor="middle" className={label}>
        {t('guest.island.label.hacienda')}
      </text>

      <rect x="236" y="176" width="32" height="20" rx="3" className="fill-surface stroke-accent" strokeWidth="1.5" />
      <text x="254" y="214" textAnchor="middle" className={label}>
        {t('guest.island.label.bungalow')}
      </text>

      <rect x="212" y="118" width="30" height="16" rx="3" className="fill-surface stroke-mark" strokeWidth="1.5" />
      <text x="228" y="112" textAnchor="middle" className={label}>
        {t('guest.island.label.pavilion')}
      </text>

      {/* pool and hot tub */}
      <ellipse cx="128" cy="164" rx="14" ry="8" className="fill-surface stroke-info" strokeWidth="1.5" />
      <text x="128" y="148" textAnchor="middle" className={soft}>
        {t('guest.island.label.pool')}
      </text>

      {/* docks reaching out over the water */}
      <path d="M300 168 H352" className="stroke-inkMuted" strokeWidth="4" strokeLinecap="round" />
      <path d="M262 274 V326" className="stroke-inkMuted" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 196 H56" className="stroke-inkMuted" strokeWidth="4" strokeLinecap="round" />
      <text x="330" y="160" textAnchor="middle" className={label}>
        {t('guest.island.label.docks')}
      </text>
    </svg>
  );
}
